import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Sparkles,
  Beaker,
  MapPin,
  Loader2,
  ArrowRight,
  ArrowLeft,
  X,
  Clock,
  HelpCircle,
} from "lucide-react";

const ICONS = {
  reset_type: Sparkles,
  peptide_readiness: Beaker,
  retreat_fit: MapPin,
};

const MoreQuizzes = ({ onOpenResetQuiz }) => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/quizzes`).then((r) => setQuizzes(r.data.quizzes));
  }, []);

  const openQuiz = async (quizId) => {
    if (quizId === "reset_type") {
      onOpenResetQuiz();
      return;
    }
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}`);
      setActiveQuiz(response.data);
      setCurrentQ(0);
      setAnswers({});
      setResult(null);
    } catch (error) {
      toast.error("Couldn't load quiz");
    }
  };

  const closeQuiz = () => {
    setActiveQuiz(null);
    setCurrentQ(0);
    setAnswers({});
    setResult(null);
    setUserInfo({ name: "", email: "" });
    setShowUserInfo(false);
  };

  const selectAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    if (currentQ < activeQuiz.questions.length - 1) {
      setTimeout(() => setCurrentQ(currentQ + 1), 300);
    } else {
      setTimeout(() => setShowUserInfo(true), 300);
    }
  };

  const submitQuiz = async () => {
    if (!userInfo.name || !userInfo.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/quizzes/submit`, {
        quiz_id: activeQuiz.id,
        name: userInfo.name,
        email: userInfo.email,
        answers,
      });
      setResult(response.data);
      setShowUserInfo(false);
    } catch (error) {
      toast.error("Couldn't submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCTA = (cta) => {
    closeQuiz();
    if (cta === "waitlist") {
      navigate("/#retreats");
    } else {
      navigate("/#programs");
    }
  };

  // Active quiz view
  if (activeQuiz) {
    const progress = result
      ? 100
      : showUserInfo
      ? 95
      : ((currentQ + 1) / activeQuiz.questions.length) * 90;
    const q = activeQuiz.questions[currentQ];
    return (
      <div className="modal-backdrop" onClick={closeQuiz}>
        <div
          className="bg-[#F9F8F6] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-[#F9F8F6] border-b border-[#E3DAC9] p-6 rounded-t-3xl">
            <div className="flex items-center justify-between mb-4">
              <span className="overline">{activeQuiz.title}</span>
              <button
                onClick={closeQuiz}
                className="p-2 hover:bg-[#E3DAC9] rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[#4A5543]" />
              </button>
            </div>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="p-8">
            {result ? (
              <div className="text-center animate-scale-in">
                <div className="w-16 h-16 rounded-full bg-[#C5A059]/20 flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-8 h-8 text-[#C5A059]" />
                </div>
                <p className="overline mb-2">Your Result</p>
                <h2 className="font-heading text-3xl text-[#2C3524] mb-2">
                  {result.result.title}
                </h2>
                {result.result.season && (
                  <p className="text-[#C5A059] italic mb-4">
                    {result.result.season}
                  </p>
                )}
                <p className="text-[#4A5543] mb-8 max-w-md mx-auto leading-relaxed">
                  {result.result.description}
                </p>
                <button
                  onClick={() => handleCTA(result.result.cta)}
                  className="btn-primary"
                  data-testid="quiz-result-cta"
                >
                  {result.result.cta === "waitlist"
                    ? "Get on the retreat list"
                    : "Explore your next step"}
                </button>
              </div>
            ) : showUserInfo ? (
              <div>
                <h3 className="font-heading text-2xl text-[#2C3524] mb-2">
                  One more step
                </h3>
                <p className="text-[#4A5543] mb-6">
                  Enter your details to see your result.
                </p>
                <div className="space-y-4">
                  <input
                    type="text"
                    value={userInfo.name}
                    onChange={(e) =>
                      setUserInfo({ ...userInfo, name: e.target.value })
                    }
                    className="input-field"
                    placeholder="Your name"
                    data-testid="extra-quiz-name"
                  />
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) =>
                      setUserInfo({ ...userInfo, email: e.target.value })
                    }
                    className="input-field"
                    placeholder="Your email"
                    data-testid="extra-quiz-email"
                  />
                </div>
                <button
                  onClick={submitQuiz}
                  disabled={submitting}
                  className="btn-primary w-full mt-6"
                  data-testid="extra-quiz-submit"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    "Get My Result"
                  )}
                </button>
              </div>
            ) : (
              <div className="animate-fade-in">
                <p className="text-sm text-[#C5A059] mb-2">
                  Question {currentQ + 1} of {activeQuiz.questions.length}
                </p>
                <h3 className="font-heading text-2xl text-[#2C3524] mb-6">
                  {q.question}
                </h3>
                <div className="space-y-3">
                  {q.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => selectAnswer(q.id, String(opt.value))}
                      className={`quiz-option w-full text-left ${
                        answers[q.id] === String(opt.value) ? "selected" : ""
                      }`}
                    >
                      <span className="text-[#2C3524]">{opt.label}</span>
                    </button>
                  ))}
                </div>
                {currentQ > 0 && (
                  <button
                    onClick={() => setCurrentQ(currentQ - 1)}
                    className="flex items-center gap-2 text-[#4A5543] mt-6 hover:text-[#C5A059]"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F9F8F6] pt-32 pb-20 px-6 md:px-12"
      data-testid="quizzes-page"
    >
      <div className="max-w-5xl mx-auto">
        <div className="max-w-2xl mb-16">
          <span className="overline mb-4 block">
            <HelpCircle className="inline-block w-3 h-3 mr-2" />
            Free Assessments
          </span>
          <h1 className="font-heading text-4xl md:text-6xl text-[#2C3524] leading-[1.1] mb-6">
            Discover your{" "}
            <span className="italic text-[#C5A059]">next step.</span>
          </h1>
          <p className="text-[#4A5543] text-lg md:text-xl font-light leading-relaxed">
            Take a quick quiz — each one is designed to give you clarity on
            exactly what to do next.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {quizzes.map((quiz) => {
            const Icon = ICONS[quiz.id] || Sparkles;
            return (
              <div
                key={quiz.id}
                data-testid={`quiz-card-${quiz.id}`}
                className="bg-white rounded-3xl p-8 border border-[#E3DAC9] hover:border-[#C5A059] transition-all hover:-translate-y-1 flex flex-col"
              >
                <Icon
                  className="w-8 h-8 text-[#C5A059] mb-6"
                  strokeWidth={1.5}
                />
                <h3 className="font-heading text-2xl text-[#2C3524] mb-3 leading-tight">
                  {quiz.title}
                </h3>
                <p className="text-[#4A5543] font-light text-sm mb-6 flex-grow">
                  {quiz.description}
                </p>
                <div className="flex items-center gap-4 text-xs text-[#4A5543]/70 tracking-wider uppercase mb-6">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {quiz.duration}
                  </span>
                  <span>•</span>
                  <span>{quiz.questions} questions</span>
                </div>
                <button
                  onClick={() => openQuiz(quiz.id)}
                  className="btn-primary w-full group"
                  data-testid={`start-quiz-${quiz.id}`}
                >
                  Start Quiz
                  <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MoreQuizzes;
